import React, { useCallback, useContext, useMemo } from 'react';
import { introspectionApi, queryApi } from 'da-graphql-client/web';
import qs from 'qs';
import ExperienceContext from '../../ExperienceContext';
import { CONSTANTS, DA_LIST_MAX_ROWS } from '../Helpers/Configuration';

const ellucianThemeEditor = 'https://theme.elluciancloud.com';
const { fetchRootTypeNames, fetchTypesByName } = introspectionApi;
const {
    fetchEedmLookupValues,
    fetchEedmValuesByAttribute,
    fetchEedmValuesByIds,
} = queryApi;


export const handleError = async (errors, isDataAccessError = false) => {
    if (Array.isArray(errors) && errors.length > 0) {
        errors = errors[0];
    }
    else if (typeof errors?.json === 'function') {
        errors = await errors?.json();
    }
    let code;
    let formatMessageId;
    if (isDataAccessError) {
        code = errors?.extensions?.code || '';
        formatMessageId = getMessageForErrorCode(code, CONSTANTS.dataAccessErrors, 'dataAccessGeneralErrorMessage') || '';
    }
    else {
        code = errors.status || errors.errorCode || errors?.error;
        formatMessageId = getMessageForErrorCode(code, CONSTANTS.apiErrors, 'adminGeneralErrorMessage') || '';
    }

    const name = errors?.name || '';
    const message = errors?.message || errors?.error || '';
    return {
        errorText: message,
        errorCode: code || 'unknown',
        formatMessageId,
        name,
        data: errors?.data || '',
    };
};

const getMessageForErrorCode = (errorCode, errorEnum, defaultMessage) => {
    return Object.values(errorEnum).find(x => x.errorCode == errorCode)?.formatMessageId ?? defaultMessage;
};

export const getApi = (apiUrl, getJwt, tenantId, getGraphApi, getIntrospectionApi) => ({
    applicationTemplates: {
        getByIdForAdmin: async (id) => {
            try {
                const res = await fetch(`${apiUrl}/${tenantId}/applicationtemplates/template-id/${id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${await getJwt()}`,
                        'Accept': 'application/json',
                    },
                });
                if (res.ok) {
                    return await res.json();
                }
                throw res;
            }
            catch (err) {
                console.error(err);
                throw await handleError(err);
            }
        },
        getById: async id => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/template-id/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getAll: async () => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/summaries`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getAllOnline: async (offset, max, sort, direction, searchString, filter) => {
            const params = qs.stringify({
                offset,
                max,
                sort,
                direction,
                searchString,
                filter,
                includeTotalCount: true
            });
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/online?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getAllDelivered: async () => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/delivered`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        checkForDistinctName:  async (name) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/distinct-names/${name}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getAllImports: async () => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/import-summaries`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        create: async (template) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(template),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        update: async (template) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(template),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        upload: async (template) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(template),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        delete: async (templateId) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/${templateId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (!res.ok) {
                    throw res;
                }
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        archive: async (templateId) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/archive/${templateId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                if (!res.ok) {
                    return res.json().then(err => {
                        return err;
                    });
                }
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        publish: async (template) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/publish`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(template),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        applicationMetrics: async (templateId) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/metrics/${templateId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        supplementalMetrics: async (templateId) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/supplemental-metrics/${templateId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getTemplatedFields: async () => {
            return fetch(`${apiUrl}/templatedfields`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getAPSSources: async (status) => {
            let url;
            if (status) {
                const params = qs.stringify({
                    status: status
                });
                url = `${apiUrl}/${tenantId}/applicationtemplates/aps-sources?${params}`;
            }
            else url = `${apiUrl}/${tenantId}/applicationtemplates/aps-sources`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getAPSTemplates: async (code) => {
            const url = `${apiUrl}/${tenantId}/applicationtemplates/aps-templates/${code}`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getTemplateList: async (offset, max, sort, direction, searchString, filter, code) => {
            const params = qs.stringify({
                offset,
                max,
                sort,
                direction,
                searchString,
                filter,
                includeTotalCount: true,
                code
            });
            const url = `${apiUrl}/${tenantId}/applicationtemplates/overview?${params}`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    applicationForms: {
        delete: async application => {
            return fetch(`${apiUrl}/${tenantId}/applications/${application.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getById: async id => {
            return fetch(`${apiUrl}/${tenantId}/applications/application-id/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        reprocessApplication: async application => {
            return fetch(`${apiUrl}/${tenantId}/applications/reprocess-application`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        retrieve: () => {
            return new Promise((resolve, reject) => {
                const result = window.localStorage.getItem('applicant-apply-autosave');
                if (!result) {
                    reject(true);
                }
                resolve(JSON.parse(result));
            });
        },
        update: async application => {
            return fetch(`${apiUrl}/${tenantId}/applications`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getByProspectId: async (prospectId, templateType) => {
            const params = qs.stringify({
                prospectId: prospectId,
                templateType: templateType
            });
            const url = `${apiUrl}/${tenantId}/applications/?${params}`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        unmaskPii: async (id, field, repetition = undefined) => {
            return fetch(`${apiUrl}/${tenantId}/applications/log-pii-unmask/${id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ field, repetition })
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        maskPii: async (id, field, repetition = undefined) => {
            return fetch(`${apiUrl}/${tenantId}/applications/log-pii-mask/${id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ field, repetition })
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        }
    },
    applicationStagers: {
        getById: async id => {
            return fetch(`${apiUrl}/${tenantId}/stagers/stager-id/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getByApplicationId: async id => {
            return fetch(`${apiUrl}/${tenantId}/stagers/application/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getList: async (offset, max, sort, direction, searchString, status, fraudFilter, code) => {
            const params = qs.stringify({
                offset,
                max,
                sort,
                direction,
                searchString,
                status: status ? status.join(',') : undefined,
                fraudFilter,
                code,
                includeTotalCount: true
            });
            const url = `${apiUrl}/${tenantId}/stagers?${params}`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getCardList: async () => {
            return fetch(`${apiUrl}/${tenantId}/stagers/summary`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        update: async stager => {
            return fetch(`${apiUrl}/${tenantId}/stagers`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(stager),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        delete: async id => {
            return fetch(`${apiUrl}/${tenantId}/stagers/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        unmaskPii: async (id, field) => {
            return fetch(`${apiUrl}/${tenantId}/stagers/log-pii-unmask/${id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ field })
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        maskPii: async (id, field) => {
            return fetch(`${apiUrl}/${tenantId}/stagers/log-pii-mask/${id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ field })
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        }
    },
    inquiries: {
        createInquiry: async (inquiry) => {
            return fetch(`${apiUrl}/${tenantId}/applications/inquiries`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(inquiry),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        }
    },
    prospects: {
        getList: async (offset, max, sort, direction, props, value) => {
            const params = qs.stringify({
                offset: offset,
                max: max,
                sort: sort,
                direction: direction,
                props: props,
                value: value
            });
            const url = `${apiUrl}/${tenantId}/prospects/?${params}`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        findOneProspectById: async id => {
            return fetch(`${apiUrl}/${tenantId}/prospects/prospect-id/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        delete: async id => {
            return fetch(`${apiUrl}/${tenantId}/prospects/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    recaptchaToken: {
        get: async () => {
            return fetch(`${apiUrl}/${tenantId}/tenants/recaptcha-tokens`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        validate: async (recaptcha_token, recaptcha_secret_key) => {
            return fetch(`${apiUrl}/${tenantId}/recaptcha/validate?recaptcha_token=${recaptcha_token}&recaptcha_secret_key=${recaptcha_secret_key}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                return res;
            }).catch(async err => {
                return err;
            });
        },
    },
    theme: {
        get: (themeName, appId) => {
            if (!appId || !themeName) {
                return Promise.reject('Ellucian Theme Editor app id and theme name must be provided');
            }
            const url = `${ellucianThemeEditor}/${appId}/theme/get?name=${themeName}`;
            return fetch(url, {
                method: 'GET',
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        list: (appId) => {
            const url = `${ellucianThemeEditor}/${appId}/theme/listThemes`;
            return fetch(url, {
                method: 'GET',
            })
                .then(res => {
                    if (res.ok) {
                        return res.json();
                    }
                    throw res;
                })
                .catch(async err => {
                    throw await handleError(err);
                });
        },
    },
    dataAccess: {
        getRootTypes: async () => {
            try {
                const res = await fetchRootTypeNames(await getIntrospectionApi());
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getTypes: async graphTypes => {
            try {
                await fetchRootTypeNames(await getIntrospectionApi());
                const res = await fetchTypesByName(await getGraphApi(), graphTypes, true);
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getLookupValues: async (graphType, filter, sort, sortOptions = { rowsPerPage: DA_LIST_MAX_ROWS }) => {
            try {
                graphType = graphType?.startsWith('F_') ? graphType.substring(2) : graphType;
                await fetchRootTypeNames(await getIntrospectionApi());
                const res = await fetchEedmLookupValues(await getGraphApi(), graphType, filter, sort, sortOptions);
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getAttributeValues: async (graphType, attributes, filter, sort, sortOptions = { rowsPerPage: DA_LIST_MAX_ROWS }, includeTotalCount = false) => {
            try {
                await fetchRootTypeNames(await getIntrospectionApi());
                const res = await fetchEedmValuesByAttribute(await getGraphApi(), graphType, attributes, filter, sort, sortOptions, includeTotalCount);
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getValuesByIds: async (graphType, idList, attributes) => {
            try {
                await fetchRootTypeNames(await getIntrospectionApi());
                const res = await fetchEedmValuesByIds(await getGraphApi(), graphType, idList, attributes);
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getGraphApi
    },
    tenant: {
        get: async () => {
            return fetch(`${apiUrl}/${tenantId}/tenants`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getApplyKey: async () => {
            return fetch(`${apiUrl}/${tenantId}/tenants/applykey`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        update: async tenant => {
            return fetch(`${apiUrl}/${tenantId}/tenants`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(tenant),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        getApiSettings: async () => {
            return fetch(`${apiUrl}/${tenantId}/tenants/api-settings`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        saveApiSettings: async (body) => {
            return fetch(`${apiUrl}/${tenantId}/tenants/api-settings`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        }
    },
    hedmSchema: {
        get: async (name, version) => {
            return fetch(`${apiUrl}/${tenantId}/applicationtemplates/hedm-schema/${name}/${version}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
    },
    communicate: {
        verify: async (email, reverifyTime = Date.now()) => {
            return fetch(`${apiUrl}/${tenantId}/communicate/verify-email`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    reverifyTime
                })
            })
                .then(r => r.json())
                .catch(async err => {
                    throw await handleError(err);
                });
        },
        reverify: async (email, reverifyTime = Date.now()) => {
            return fetch(`${apiUrl}/${tenantId}/communicate/reverify-email`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    reverifyTime
                })
            })
                .then(r => r.json())
                .catch(async err => {
                    throw await handleError(err);
                });
        },
        sendEmail: async (sender, recipient, subject, message) => {
            return fetch(`${apiUrl}/${tenantId}/communicate/send-email`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sender,
                    recipient,
                    subject,
                    message
                })
            })
                .then(r => r.json())
                .catch(async err => {
                    throw await handleError(err);
                });
        },
        getAllEmailVerifications: async () => {
            return fetch(`${apiUrl}/${tenantId}/communicate/email-verifications`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            })
                .then(r => r.json())
                .catch(async err => {
                    throw await handleError(err);
                });
        },
        getVerifiedEmails: async () => {
            return fetch(`${apiUrl}/${tenantId}/communicate/email-verifications/verified`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            })
                .then(r => r.json())
                .catch(async err => {
                    throw await handleError(err);
                });
        },
        deleteVerification: async (email) => {
            return fetch(`${apiUrl}/${tenantId}/communicate/email-verification?email=${email}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            })
                .then(r => r.json())
                .catch(async err => {
                    throw await handleError(err);
                });
        }
    },
    validatedEmail: {
        delete: async (email) => {
            return fetch(`${apiUrl}/${tenantId}/communicate/validated-email/${email}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                if (!res.ok) {
                    return res.json().then(err => {
                        return err;
                    });
                }
            }).catch(async err => {
                throw await handleError(err);
            });
        }
    },
    reports: {
        getPaymentsReport: async (offset, max, sort, direction, statusFilter, includeTotalCount) => {
            let qParams = offset || offset === 0 ? `offset=${offset}&` : '';
            qParams = max ? `${qParams}&max=${max}&` : qParams;
            qParams = sort ? `${qParams}&sort=${sort}&` : qParams;
            qParams = direction ? `${qParams}&direction=${direction}&` : qParams;
            qParams = statusFilter ? `${qParams}&statusFilter=${statusFilter}&` : qParams;
            qParams = includeTotalCount ? `${qParams}&includeTotalCount=true` : qParams;
            qParams = qParams ? `?${qParams.split('&').filter(Boolean).join('&')}` : '';
            return fetch(`${apiUrl}/${tenantId}/applications/payments${qParams}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
            }).catch(err => {
                throw handleError(err);
            });
        },
        getSubmittedAppsByMonth: async (offset, max, sort, direction, includeTotalCount) => {
            let qParams = offset || offset === 0 ? `offset=${offset}&` : '';
            qParams = max ? `${qParams}&max=${max}&` : qParams;
            qParams = sort ? `${qParams}&sort=${sort}&` : qParams;
            qParams = direction ? `${qParams}&direction=${direction}&` : qParams;
            qParams = includeTotalCount ? `${qParams}&includeTotalCount=true` : qParams;
            qParams = qParams ? `?${qParams.split('&').filter(Boolean).join('&')}` : '';
            return fetch(`${apiUrl}/${tenantId}/applications/submitted-report${qParams}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
            }).catch(err => {
                throw handleError(err);
            });
        },
        getRecaptchaErrorLog: async (offset = 0, max = 20, sort = 'createdAt', direction = 'desc', includeTotalCount) => {
            return fetch(`${apiUrl}/${tenantId}/errorlogs?offset=${offset}&max=${max}&sort=${sort}&direction=${direction}&includeTotalCount=${includeTotalCount}&type=recaptcha`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${await getJwt()}`,
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    applicantLogout: () => {
        return fetch(`${apiUrl}/${tenantId}/applicantlogout`, {
            method: 'GET'
        }).catch(async err => {
            throw await handleError(err);
        });
    }
});

const ApiContext = React.createContext(getApi(
    '',
    () => Promise.resolve(''),
    '',
    () => Promise.resolve({}),
    () => Promise.resolve({})
));
const ApiProvider = (props) => {
    const experienceContext = useContext(ExperienceContext);
    const apiUrl = useMemo(() => {
        switch (document.location.hostname) {
            case 'experience-dev-devinternal.elluciancloud.com':
                return (
                    experienceContext?.configuration &&
                    experienceContext.configuration[CONSTANTS.apiUrlKey]
                ) || 'https://applicant-apply-api.test.10321.elluciancloud.com';
            case 'experience-test-internal.elluciancloud.com':
                return `https://applicant-apply-api.${process.env.STAGE}.${process.env.ACCOUNT_ID}.elluciancloud.com`;
            default:
                return 'https://applicant-apply-api.applicant-apply.elluciancloud.com';
        }
    }, []);

    const getGraphApi = useCallback(async () => ({
        cacheKey: 'Apply',
        webFetch: {
            graphQL: `${apiUrl}/${experienceContext.tenantId || ''}/query`,
            options: {
                headers: {
                    'Authorization': `Bearer ${await experienceContext.getJwt()}`,
                }
            }
        }
    }), [experienceContext.tenantId, experienceContext.getJwt]);
    const getIntrospectionApi = useCallback(async () => ({
        cacheKey: 'Apply',
        webFetch: {
            graphQL: `${apiUrl}/${experienceContext.tenantId || ''}/query/graphqlIntrospection`,
            options: {
                headers: {
                    'Authorization': `Bearer ${await experienceContext.getJwt()}`,
                }
            }
        }
    }), [experienceContext.tenantId, experienceContext.getJwt]);
    const api = useMemo(
        () => getApi(apiUrl, experienceContext.getJwt, experienceContext.tenantId, getGraphApi, getIntrospectionApi),
        [experienceContext.getJwt, experienceContext.tenantId, getGraphApi]
    );

    return (
        <ApiContext.Provider value={api}>{props.children}</ApiContext.Provider>
    );
};
const useApi = () => {
    const ctx = useContext(ApiContext);
    if (ctx === undefined) {
        throw new Error('useApi must be used within a ApiProvider');
    }
    return ctx;
};
export { ApiProvider, useApi };